import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { Company, DocumentType, Invoice } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { useFormHelpers } from 'hooks/useFormHelpers';

import { DocumentConfig } from 'utils/config/documentEntries';
import {
  CONFIDENCE_STATUS_COLORS,
  getConfidenceFieldSx
} from 'utils/constants/confidenceStatusColors';
import { DOCUMENT_TYPES_TRANSLATION_KEYS } from 'utils/constants/invoices';
import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';
import { ConfidenceLevel } from 'utils/enums/Confidence';
import { getFieldConfidenceUi } from 'utils/helpers/confidenceHelpers';
import { getInitiationSummary } from 'utils/helpers/invoiceSectionSummaryHelpers';

import { arrayFieldFormContainer } from 'styles/components/Common';
import { verificationFormItem } from 'styles/pages/InvoiceVerificationStyle';

import { InvoiceVerificationCard } from './InvoiceVerificationCard';

interface InitiationCardProps {
  allCompanies: Company[];
  currentInvoice: Invoice;
  documentConfig: DocumentConfig;
  confidenceLevel?: ConfidenceLevel;
  expanded?: boolean;
  onToggle?: () => void;
  onExpanded?: () => void;
  autoExpandReason?: string;
  hasError?: boolean;
  onReceiverChange?: (companyId: number) => void;
}

export const InitiationCard = ({
  allCompanies,
  currentInvoice,
  documentConfig,
  confidenceLevel,
  expanded,
  onToggle,
  onExpanded,
  autoExpandReason,
  hasError,
  onReceiverChange
}: InitiationCardProps) => {
  const participant = documentConfig.getParticipant();
  const { translate } = useTranslations();
  const { getOptionLabelReceiverName } = useFormHelpers();
  const { control } = useFormContext();

  const companyId = useWatch({
    name: InvoiceVerificationFieldName.CompanyId
  });
  const documentType = useWatch({
    name: InvoiceVerificationFieldName.DocumentType
  });

  const summary = useMemo(
    () =>
      getInitiationSummary(
        { companyId, documentType, allCompanies },
        translate
      ),
    [companyId, documentType, allCompanies, translate]
  );

  const receiverLabel = translate('labels.' + participant) + '*';
  const documentTypeLabel = translate('labels.documentType') + '*';

  return (
    <InvoiceVerificationCard
      title={translate('labels.initiation')}
      summary={summary}
      confidenceLevel={confidenceLevel}
      expanded={expanded}
      onToggle={onToggle}
      onExpanded={onExpanded}
      autoExpandReason={autoExpandReason}
      hasError={hasError}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'column', lg: 'row' },
          gap: 2
        }}
      >
        <Box sx={{ width: { sm: '100%', lg: '50%' } }}>
          {allCompanies?.length > 0 && (
            <Box sx={{ ...arrayFieldFormContainer, flexDirection: 'column' }}>
              <Controller
                name={InvoiceVerificationFieldName.CompanyId}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error, isDirty } }) => {
                  const { isLow, isMedium, confidenceMessage } =
                    getFieldConfidenceUi(
                      currentInvoice,
                      InvoiceVerificationFieldName.CompanyId,
                      translate,
                      isDirty
                    );
                  const isEmpty = !field.value;
                  const hasRequiredError = !!error || isEmpty;
                  const confidenceTone = hasRequiredError
                    ? null
                    : isLow
                      ? 'lowConfidence'
                      : isMedium
                        ? 'needsReview'
                        : null;
                  const showError = hasRequiredError || isLow;
                  const isWarning = confidenceTone === 'needsReview';

                  return (
                    <FormControl
                      sx={
                        confidenceTone
                          ? getConfidenceFieldSx(
                              verificationFormItem,
                              confidenceTone
                            )
                          : verificationFormItem
                      }
                      fullWidth
                      error={showError}
                      color={isWarning ? 'warning' : undefined}
                    >
                      <InputLabel id="receiver-select-label">
                        {receiverLabel}
                      </InputLabel>
                      <Select
                        {...field}
                        onChange={(event) => {
                          const newCompanyId = event.target.value as number;
                          if (newCompanyId !== field.value) {
                            onReceiverChange?.(newCompanyId);
                          }
                          field.onChange(event);
                        }}
                        id="receiver-select"
                        labelId="receiver-select-label"
                        sx={
                          confidenceTone
                            ? getConfidenceFieldSx(
                                { textAlign: 'left' },
                                confidenceTone
                              )
                            : { textAlign: 'left' }
                        }
                        label={receiverLabel}
                        error={showError}
                        color={isWarning ? 'warning' : undefined}
                      >
                        {allCompanies.map((company: Company) => (
                          <MenuItem key={company.id} value={company.id}>
                            {getOptionLabelReceiverName(company)}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText
                        error={showError}
                        sx={{
                          color: confidenceTone
                            ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                            : undefined
                        }}
                      >
                        {hasRequiredError
                          ? translate('errors.required')
                          : (confidenceMessage?.text ?? '')}
                      </FormHelperText>
                    </FormControl>
                  );
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ width: { sm: '100%', lg: '50%' } }}>
          <Controller
            name={InvoiceVerificationFieldName.DocumentType}
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error, isDirty } }) => {
              const { isLow, isMedium, confidenceMessage } =
                getFieldConfidenceUi(
                  currentInvoice,
                  InvoiceVerificationFieldName.DocumentType,
                  translate,
                  isDirty
                );
              const isEmpty = !field.value;
              const hasRequiredError = !!error || isEmpty;
              const confidenceTone = hasRequiredError
                ? null
                : isLow
                  ? 'lowConfidence'
                  : isMedium
                    ? 'needsReview'
                    : null;
              const showError = hasRequiredError || isLow;
              const isWarning = confidenceTone === 'needsReview';

              return (
                <FormControl
                  fullWidth
                  sx={
                    confidenceTone
                      ? getConfidenceFieldSx(
                          verificationFormItem,
                          confidenceTone
                        )
                      : verificationFormItem
                  }
                  error={showError}
                  color={isWarning ? 'warning' : undefined}
                >
                  <InputLabel id="document-type-select-label">
                    {documentTypeLabel}
                  </InputLabel>
                  <Select
                    {...field}
                    id="document-type-select"
                    labelId="document-type-select-label"
                    sx={
                      confidenceTone
                        ? getConfidenceFieldSx(
                            { textAlign: 'left' },
                            confidenceTone
                          )
                        : { textAlign: 'left' }
                    }
                    label={documentTypeLabel}
                    error={showError}
                    color={isWarning ? 'warning' : undefined}
                  >
                    {(Object.values(DocumentType) as DocumentType[])
                      .filter((docType) => docType !== DocumentType.NO_DOCUMENT)
                      .map((docType) => (
                        <MenuItem key={docType} value={docType}>
                          {translate(DOCUMENT_TYPES_TRANSLATION_KEYS[docType])}
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText
                    error={showError}
                    sx={{
                      color: confidenceTone
                        ? CONFIDENCE_STATUS_COLORS[confidenceTone].text
                        : undefined
                    }}
                  >
                    {hasRequiredError
                      ? translate('errors.required')
                      : (confidenceMessage?.text ?? '')}
                  </FormHelperText>
                </FormControl>
              );
            }}
          />
        </Box>
      </Box>
    </InvoiceVerificationCard>
  );
};
